import { Button, ComboBox, ContainedList, ContainedListItem, InlineLoading, InlineNotification } from '@carbon/react';
import { Search } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useEffect, useRef, useState } from 'react';
import type { AddressLookupSource, AddressSuggestion, FoundAddress } from '../adminContext';

const PAUSE_MS = 300;

const newToken = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());

/**
 * Finds the latitude and longitude of an address through the maps provider: address suggestions after a short pause in typing
 * (from 2 characters; streets and buildings, not businesses), the one picked looked up with the same session token (one billed
 * search); or *Find* geocodes the typed text, filling the form at once on one match and listing several to pick from.
 */
export function AddressLookupField({
  id,
  source,
  onFound,
}: {
  id: string;
  source: AddressLookupSource;
  onFound(address: FoundAddress): void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<readonly AddressSuggestion[]>([]);
  const [found, setFound] = useState<readonly FoundAddress[]>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const token = useRef(newToken());
  const typed = text.trim();
  const canFind = typed.length >= 2 && !busy;

  useEffect(() => {
    if (typed.length < 2) return; // nothing is asked; the list shows nothing (below)
    let live = true;
    const timer = window.setTimeout(() => {
      source
        .suggest(typed, token.current)
        .then((list) => {
          if (!live) return;
          setSuggestions(list);
          setProblem(undefined);
        })
        .catch(() => live && setProblem(t('admin.addressLookup.unavailable')));
    }, PAUSE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [typed, source, t]);

  const run = async (lookup: () => Promise<void>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await lookup();
    } catch {
      setFound(undefined);
      setProblem(t('admin.addressLookup.unavailable'));
    } finally {
      setBusy(false);
    }
  };

  const pickSuggestion = (suggestion: AddressSuggestion | string | null | undefined) => {
    if (!suggestion || typeof suggestion === 'string') return; // the typed text itself: Find looks it up
    void run(async () => {
      try {
        onFound(await source.resolve(suggestion.placeId, token.current));
        setFound(undefined);
      } finally {
        token.current = newToken(); // the next search is a new session
      }
    });
  };

  const find = () => {
    if (!canFind) return;
    void run(async () => {
      const results = await source.find(typed);
      const [only, ...more] = results;
      if (only && more.length === 0) {
        onFound(only);
        setFound(undefined);
      } else {
        setFound(results);
      }
    });
  };

  const pickFound = (address: FoundAddress) => {
    onFound(address);
    setFound(undefined);
  };

  return (
    <div className="pos-address-lookup">
      <div className="pos-address-lookup__row">
        <ComboBox<AddressSuggestion>
          id={id}
          titleText={t('admin.addressLookup.label')}
          placeholder={t('admin.addressLookup.placeholder')}
          helperText={t('admin.addressLookup.help')}
          items={(typed.length < 2 ? [] : suggestions) as AddressSuggestion[]}
          itemToString={(s) => (s ? [s.mainText, s.secondaryText].filter(Boolean).join(', ') : '')}
          shouldFilterItem={() => true}
          allowCustomValue
          onInputChange={(value) => setText(value ?? '')}
          onChange={({ selectedItem }) => pickSuggestion(selectedItem)}
        />
        {busy ? (
          <InlineLoading description={t('admin.addressLookup.finding')} />
        ) : (
          <Button kind="tertiary" renderIcon={Search} disabled={!canFind} onClick={find}>
            {t('admin.addressLookup.find')}
          </Button>
        )}
      </div>
      {found && found.length === 0 && (
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('admin.addressLookup.none')} />
      )}
      {found && found.length > 1 && (
        <ContainedList label={t('admin.addressLookup.pick')} kind="on-page">
          {found.map((a) => (
            <ContainedListItem key={`${a.latitude},${a.longitude},${a.formattedAddress}`} onClick={() => pickFound(a)}>
              {a.formattedAddress}
            </ContainedListItem>
          ))}
        </ContainedList>
      )}
      {problem && <InlineNotification kind="warning" lowContrast hideCloseButton title={problem} />}
    </div>
  );
}
